// Namespaces
import { ProposalDAOInterface } from '../ProposalDAO';
import { ComparisonItemBasedProposalDAO } from '../ProposalDAOImpl';
import { ComparisonItemDAO } from '../ProposalItemDAO';
import * as ProposalNS from '../../Proposal';
import * as ProposalElementNS from '../../ProposalElements';
import { DataPoints, DataPointResponse } from '../DataPoints';
import { ProposalStyleClassNames } from '../../ProposalStyleClassNames';
import { ProposalFieldDAO } from '../ProposalFieldDAO';

// Components
import { ProposalItemLabelCell } from '@/views/components/cells';

// Types
import ChartItem from '@/types/chart-item';
import ProposalToolbarOption from '@/types/proposal-toolbar-option';
import LineSeriesData from '@/types/line-series-data';
import RollingIntervals from '@/types/rolling-intervals';
import LineSeriesDataPoint from '@/types/line-series-data-point';

// Utils
import { mergeChartItems, getRollingIntervalFromDates, getGraphStartDate, getGraphData } from '@/utils/charts';
import { ROLLING_INTERVALS } from '@/utils/portfolios';
import { currencyToText } from '@/utils/currency';
import { TIME_PERIODS } from '@/utils/constants';
import { PROPOSAL_DISCLAIMERS } from '../../utils/constants';
import { monthsToPeriod } from '@/utils/time';

export default class DrawdownAnalysisDAO extends ComparisonItemBasedProposalDAO {
  public static buildInstance(proposal: ProposalNS.Proposal, proposalElement: ProposalElementNS.ProposalDataElement): ProposalDAOInterface {
    return new DrawdownAnalysisDAO(proposal, proposalElement);
  }

  series: LineSeriesDataPoint[][];

  toolbarOption: ProposalToolbarOption | undefined;

  formattedChartItems: ChartItem[];

  interval: RollingIntervals;

  out: LineSeriesData[] = [];

  constructor(proposal: ProposalNS.Proposal, proposalElement: ProposalElementNS.ProposalDataElement) {
    super(proposal, proposalElement);
    this.toolbarOption = {};
    this.series = [];
    this.formattedChartItems = [];
    this.interval = ROLLING_INTERVALS.WEEKLY;
  }

  /* eslint-disable-next-line class-methods-use-this */
  protected getColorKeys(): string[] {
    return [];
  }

  protected build(): void {
    this.toolbarOption = this.getToolbarOption();
    if (this.toolbarOption !== undefined) {
      const { startDate, endDate, useMonthly } = this.toolbarOption;
      const { shortHistory, showBaseCurrencies } = this.proposal;

      const monthsRange = this.toolbarOption?.monthsRange;
      const period = monthsRange && monthsToPeriod(monthsRange);

      const showSinceInceptionDisclaimer = period === TIME_PERIODS.INC && shortHistory;
      const showMaxCommonDisclaimer = period === TIME_PERIODS.CI && !shortHistory;

      if (endDate) {
        const itemDAOs: ComparisonItemDAO[] = this.getItemDAOs();
        const items = itemDAOs.map(i => i.item);
        const chartItems = mergeChartItems(items, []);
        const earliestStartDate = startDate !== null ? getGraphStartDate(chartItems, startDate) : null;
        let disclaimerKeys: string[] = [];

        this.interval = getRollingIntervalFromDates(earliestStartDate, endDate, items, useMonthly, shortHistory);
        this.formattedChartItems = getGraphData(chartItems, earliestStartDate, endDate, this.interval, undefined, shortHistory);

        for (const item of this.formattedChartItems) {
          const dpResponse: DataPointResponse<LineSeriesDataPoint[]> = DataPoints.getDrawdownAnalysisSeries(item, this.interval, showBaseCurrencies === true);
          this.series.push(dpResponse.response);
          if (dpResponse.disclaimerKeys.length > 0) {
            disclaimerKeys = [
              ...dpResponse.disclaimerKeys,
              ...(showSinceInceptionDisclaimer ? [PROPOSAL_DISCLAIMERS.SINCE_INCEPTION_DATE] : []),
              ...(showMaxCommonDisclaimer ? [PROPOSAL_DISCLAIMERS.COMMON_INCEPTION_DATE] : []),
            ];
          }
        }

        for (const disclaimerKey of disclaimerKeys) {
          this.addDisclaimerConfigs([
            {
              key: disclaimerKey,
              config: { series: this.series, interval: this.interval },
              items: itemDAOs.map(itemDAO => itemDAO.item),
            },
          ]);
        }
      }
    }

    let i: number = 0;
    let baseDAO: ComparisonItemDAO | null = null;
    let itemDAO = null;
    while ((itemDAO = this.nextItem()) !== null) {
      if (baseDAO === null) {
        baseDAO = itemDAO;
      }
      const nameSuffix = this.proposal.showBaseCurrencies ? ` (${currencyToText(itemDAO.item.currency)})` : '';

      const entry: LineSeriesData = {
        data: this.series[i],
        color: itemDAO.item.color || '',
        name: `${this.formatLocalizedString(itemDAO.item.name)}${nameSuffix}`,
        dashStyle: itemDAO.item.type === ProposalNS.ProposalItemType.Benchmark ? 'Dash' : 'Solid',
        lineWidth: itemDAO.item.type === ProposalNS.ProposalItemType.Benchmark ? 1 : 2,
        tooltip: {
          valueDecimals: 2,
          valueSuffix: this.proposal.showBaseCurrencies ? `% ${itemDAO.item.currency}` : '%',
        },
      };

      this.out.push(entry);
      this.addItemField(baseDAO, itemDAO);

      i++;
    }
  }

  getLineSeries(): LineSeriesData[] {
    return this.out;
  }

  private addItemField(baseDAO: ComparisonItemDAO, itemDAO: ComparisonItemDAO) {
    const name = this.formatLocalizedString(itemDAO.item.name);
    const field: ProposalFieldDAO = new ProposalFieldDAO('', name, ProposalStyleClassNames.ProposalTableContent);
    field.cellType = ProposalItemLabelCell;
    field.color = itemDAO.item.color;
    field.itemType = itemDAO.item.type;
    field.flags = itemDAO.item.flags;
    field.currency = this.proposal.showBaseCurrencies ? itemDAO.item.currency : undefined;

    this.addFieldDAO(baseDAO, field);
  }
}
